import React, { useState } from 'react';
import ShopLayout from '../../components/shop/ShopLayout';
import { MapPin, Phone, Clock, Navigation, Search, Store } from 'lucide-react';

function ShopStores() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');

  const stores = [
    {
      id: 1,
      name: 'Zomart Gurgaon (Corporate)',
      type: 'COCO',
      address: 'Ground Floor, Plot No. 123, Sector 44, Gurgaon, Haryana - 122003',
      city: 'Gurgaon',
      phone: '+91 98765 43210',
      hours: 'Mon-Sat: 10:00 AM - 8:00 PM',
      mapUrl: 'https://maps.google.com/?q=28.4594965,77.0688854',
      products: ['ZOPRO', 'ZRP']
    },
    {
      id: 2,
      name: 'Zomart Delhi (Nehru Place)',
      type: 'COCO',
      address: 'Shop No. 45, Nehru Place, New Delhi - 110019',
      city: 'Delhi',
      phone: '+91 98765 43211',
      hours: 'Mon-Sat: 10:00 AM - 8:00 PM',
      mapUrl: 'https://maps.google.com/?q=28.5494,77.2517',
      products: ['ZOPRO', 'ZRP']
    },
    {
      id: 3,
      name: 'Zomart PLUS Noida',
      type: 'FOFO',
      address: 'Sector 18, Noida, Uttar Pradesh - 201301',
      city: 'Noida',
      phone: '+91 98765 43212',
      hours: 'Mon-Sat: 11:00 AM - 9:00 PM',
      mapUrl: 'https://maps.google.com/?q=28.5706,77.3215',
      products: ['ZOPRO', 'ZRP']
    },
    {
      id: 4,
      name: 'Zomart PRO Jaipur',
      type: 'FOCO',
      address: 'C-Scheme, Jaipur, Rajasthan - 302001',
      city: 'Jaipur',
      phone: '+91 98765 43213',
      hours: 'Mon-Sat: 10:00 AM - 8:00 PM',
      mapUrl: 'https://maps.google.com/?q=26.9124,75.7873',
      products: ['ZOPRO']
    },
    {
      id: 5,
      name: 'Zomart MAXX Lucknow',
      type: 'FOCO',
      address: 'Hazratganj, Lucknow, Uttar Pradesh - 226001',
      city: 'Lucknow',
      phone: '+91 98765 43214',
      hours: 'Mon-Sat: 10:00 AM - 9:00 PM',
      mapUrl: 'https://maps.google.com/?q=26.8467,80.9462',
      products: ['ZOPRO', 'ZRP']
    },
  ];

  const cities = ['all', ...new Set(stores.map(s => s.city))];

  const filteredStores = stores.filter(store => {
    const matchesCity = selectedCity === 'all' || store.city === selectedCity;
    const matchesSearch = store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         store.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         store.city.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCity && matchesSearch;
  });

  const getStoreTypeBadge = (type) => {
    const badges = {
      'COCO': { label: 'Company Store', color: 'bg-blue-100 text-blue-700' },
      'FOFO': { label: 'Franchise', color: 'bg-green-100 text-green-700' },
      'FOCO': { label: 'Partner Store', color: 'bg-purple-100 text-purple-700' },
    };
    return badges[type] || badges['COCO'];
  };

  return (
    <ShopLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Find a Store</h1>
            <p className="text-xl text-white/80">
              Visit our Zomart stores to experience products in person
            </p>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-6 bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by store name, location..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {cities.map(city => (
                <option key={city} value={city}>
                  {city === 'all' ? 'All Cities' : city}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Store List */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-6">
            <p className="text-gray-600">{filteredStores.length} stores found</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStores.map(store => {
              const badge = getStoreTypeBadge(store.type);
              return (
                <div key={store.id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                          <Store className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{store.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${badge.color}`}>
                            {badge.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-600">{store.address}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <a href={`tel:${store.phone}`} className="text-gray-600 hover:text-primary">
                          {store.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <p className="text-gray-600">{store.hours}</p>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-gray-500 mb-2">Available Products:</p>
                      <div className="flex gap-2">
                        {store.products.map(product => (
                          <span
                            key={product}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              product === 'ZOPRO' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}
                          >
                            {product}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-3 bg-gray-50 border-t">
                    <a
                      href={store.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-primary font-medium hover:underline"
                    >
                      <Navigation className="w-4 h-4" />
                      Get Directions
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredStores.length === 0 && (
            <div className="text-center py-12">
              <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No stores found</h3>
              <p className="text-gray-500">Try adjusting your search or filter</p>
            </div>
          )}
        </div>
      </section>

      {/* Franchise CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-primary to-blue-700 rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Own a Zomart Franchise</h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Join India's fastest growing refurbished tech retail network. Low investment, high returns.
            </p>
            <a
              href="/shop/franchise"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-primary rounded-full font-semibold hover:bg-gray-100 transition"
            >
              Enquire Now
            </a>
          </div>
        </div>
      </section>
    </ShopLayout>
  );
}

export default ShopStores;
